import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import BackToTop from "./components/BackToTop";
import CookieConsent from "./components/CookieConsent";
import RouteTracker from "./components/RouteTracker";
import LanguageSync from "./components/LanguageSync";
import { initializeConsent } from "@/lib/analytics";
import Index from "./pages/Index";
import About from "./pages/About";
import Contact from "./pages/Contact";
import News from "./pages/News";
import Services from "./pages/Services";
import AISkilling from "./pages/AISkilling";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import StudentStudyLogitech from "./pages/StudentStudyLogitech";
import NotFound from "./pages/NotFound";
import Unsubscribe from "./pages/Unsubscribe";

const queryClient = new QueryClient();

// The same set of routes is mounted under both `/` (English) and `/sv` (Swedish).
// LanguageSync reads the URL and switches i18next accordingly.
const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Index />} />
    <Route path="/about" element={<About />} />
    <Route path="/ai-skilling" element={<AISkilling />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/news" element={<News />} />
    <Route path="/services" element={<Services />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/student-study-logitech" element={<StudentStudyLogitech />} />
  </Routes>
);

const App = () => {
  // Initialize Google Consent Mode v2 on app load
  useEffect(() => {
    initializeConsent();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LanguageSync />
          <ScrollToTop />
          <RouteTracker />
          <BackToTop />
          <CookieConsent />
          <Routes>
            {/* Swedish mirror */}
            <Route path="/sv" element={<Index />} />
            <Route path="/sv/about" element={<About />} />
            <Route path="/sv/ai-skilling" element={<AISkilling />} />
            <Route path="/sv/contact" element={<Contact />} />
            <Route path="/sv/news" element={<News />} />
            <Route path="/sv/services" element={<Services />} />
            <Route path="/sv/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/sv/student-study-logitech" element={<StudentStudyLogitech />} />

            {/* English (default) */}
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/ai-skilling" element={<AISkilling />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/news" element={<News />} />
            <Route path="/services" element={<Services />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/student-study-logitech" element={<StudentStudyLogitech />} />

            {/* Email unsubscribe (shared EN/SV) */}
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/sv/unsubscribe" element={<Unsubscribe />} />

            {/* Old website URL redirects */}
            <Route path="/17/4/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/17/4/about-us/" element={<Navigate to="/about" replace />} />
            <Route path="/17/5/news" element={<Navigate to="/news" replace />} />
            <Route path="/17/5/news/" element={<Navigate to="/news" replace />} />
            <Route path="/17/6/services" element={<Navigate to="/services" replace />} />
            <Route path="/17/6/services/" element={<Navigate to="/services" replace />} />
            <Route path="/17/7/our-team" element={<Navigate to="/about" replace />} />
            <Route path="/17/7/our-team/" element={<Navigate to="/about" replace />} />
            <Route path="/17/8/contact" element={<Navigate to="/contact" replace />} />
            <Route path="/17/8/contact/" element={<Navigate to="/contact" replace />} />
            <Route path="/17/8/contact//" element={<Navigate to="/contact" replace />} />

            {/* Additional legacy URL redirects (from Google Search Console) */}
            <Route path="/Kontakter" element={<Navigate to="/contact" replace />} />
            <Route path="/Kontakter/" element={<Navigate to="/contact" replace />} />
            <Route path="/17/1/home" element={<Navigate to="/" replace />} />
            <Route path="/17/1/home/" element={<Navigate to="/" replace />} />
            <Route path="/17/2/reports" element={<Navigate to="/news" replace />} />
            <Route path="/17/2/reports/" element={<Navigate to="/news" replace />} />
            <Route path="/Hybrida-arbetssätt-och-AI" element={<Navigate to="/news" replace />} />
            <Route path="/Hybrida-arbetssätt-och-AI/" element={<Navigate to="/news" replace />} />
            <Route path="/Student" element={<Navigate to="/ai-skilling" replace />} />
            <Route path="/Student/" element={<Navigate to="/ai-skilling" replace />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
  );
};

export default App;
